import { useEffect, useState, useCallback } from "react";
import { Clock, Plus, Loader2, AlertTriangle, ShieldAlert, Phone, MapPin } from "lucide-react";
import { DataTable } from "@/components/common/DataTable";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { fdate } from "@/lib/format";
import { MoneyInput } from "@/components/common/MoneyInput";
import { toast } from "sonner";

const CANALES = ["llamada", "WhatsApp", "SMS", "email", "visita"];
const RESULTADOS = [
  { v: "contacto", l: "Contacto exitoso" },
  { v: "promesa", l: "Promesa de pago" },
  { v: "sin_contacto", l: "Sin contacto" },
  { v: "ilocalizable", l: "Ilocalizable" },
  { v: "negativa", l: "Negativa a pagar" },
];

export function GestionesTab({ can, initialCreditoId = "" }) {
  const puedeEditar = can("cobranza", "editar") || can("cobranza", "crear");
  const [fichas, setFichas] = useState([]);
  const [combo, setCombo] = useState({ internos: [], externos: [], despachos_internos: [] });
  const [zonasCfg, setZonasCfg] = useState(null);
  const [fuera, setFuera] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [ficha, setFicha] = useState(null);
  const [form, setForm] = useState(null);
  const [gestor, setGestor] = useState("");
  const [busy, setBusy] = useState(false);

  const cargar = useCallback(async () => {
    const [lst, c, fh, z] = await Promise.all([
      api.get("/cobranza/lista", { params: { solo_vencidos: 0 } }).then((r) => r.data).catch(() => []),
      api.get("/cobranza/gestores-combo").then((r) => r.data).catch(() => ({ internos: [], externos: [], despachos_internos: [] })),
      api.get("/cobranza/gestiones-fuera-horario").then((r) => r.data).catch(() => []),
      api.get("/cobranza/zonas-horarias-mx").then((r) => r.data).catch(() => null),
    ]);
    setFichas(lst);
    setCombo(c);
    setFuera(fh);
    setZonasCfg(z);
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  // Ficha del cliente asociado al crédito seleccionado (para saber su estado/municipio)
  const [clienteInfo, setClienteInfo] = useState(null);
  useEffect(() => {
    if (!ficha?.cliente_id) { setClienteInfo(null); return; }
    api.get(`/clientes/${ficha.cliente_id}`).then((r) => setClienteInfo(r.data))
      .catch(() => setClienteInfo(null));
  }, [ficha?.cliente_id]);

  // Municipios estacionales sugeridos según el estado del deudor
  const municipiosSugeridos = (() => {
    if (!zonasCfg || !clienteInfo?.entidad_federativa) return null;
    const est = clienteInfo.entidad_federativa;
    // Todas las entradas del catálogo cuyo estado (o estado_matchea) coincida
    const grupos = Object.entries(zonasCfg.municipios_estacionales || {})
      .filter(([k, cfg]) => (cfg.estado_matchea || k) === est);
    if (grupos.length === 0) return null;
    const opciones = [];
    grupos.forEach(([, cfg]) => {
      if (cfg.todo_el_estado) return;
      (cfg.municipios || []).forEach((m) => opciones.push({
        municipio: m, articulo: cfg.articulo, tz_verano: cfg.tz_verano, tz_estandar: cfg.tz_estandar,
      }));
    });
    return opciones.length > 0 ? opciones : null;
  })();

  const openAlta = (f) => {
    setFicha(f);
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    setForm({
      fecha: now.toISOString().slice(0, 10),
      hora_gestion: `${hh}:${mm}`,
      canal: "llamada",
      resultado: "contacto",
      promesa_fecha: "",
      promesa_monto: 0,
      notas: "",
      evidencia: "",
      justificacion_fuera_horario: "",
      municipio: "",
    });
    setGestor("");
    setDlgOpen(true);
  };

  const guardar = async () => {
    if (!form.hora_gestion || !form.hora_gestion.includes(":")) {
      toast.error("Captura la hora (HH:MM)"); return;
    }
    setBusy(true);
    try {
      let payload = { ...form };
      // "__otro__" es marcador UI para "municipio no fronterizo" — no se manda al backend.
      if (payload.municipio === "__otro__") payload.municipio = "";
      if (gestor.startsWith("user:")) {
        payload.gestor_id = gestor.slice(5);
        const it = combo.internos.find((x) => x.value === gestor);
        payload.gestor = it?.label || "";
      } else if (gestor.startsWith("desp:")) {
        const [, dId, pId] = gestor.split(":");
        payload.despacho_id = dId;
        if (pId) payload.persona_ejecutora_id = pId;
        const it = combo.externos.find((x) => x.value === gestor)
          || combo.despachos_internos.find((x) => x.value === gestor);
        payload.gestor = it?.label || "";
      }
      const { data } = await api.post(`/cobranza/${ficha.credito_id}/gestion-redeco`, payload);
      toast.success(data.dentro_horario ? "Gestión registrada" : "Gestión registrada FUERA DE HORARIO (justificada)");
      setDlgOpen(false);
      await cargar();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det && typeof det === "object" && det.codigo === "fuera_horario_sin_justificacion") {
        toast.warning(det.mensaje);
      } else {
        toast.error(typeof det === "string" ? det : "No se pudo registrar la gestión");
      }
    } finally { setBusy(false); }
  };

  const COLS = [
    { key: "numero_credito", label: "Crédito" },
    { key: "cliente", label: "Cliente" },
    { key: "dias_atraso", label: "Días" },
    { key: "gestor", label: "Gestor actual", render: (r) => r.gestor || <span className="text-slate-400">Sin asignar</span> },
    { key: "ultima_gestion", label: "Última gestión", render: (r) => r.ultima_gestion
      ? <span className="text-xs">{fdate(r.ultima_gestion.fecha)} {r.ultima_gestion.hora}{r.ultima_gestion.dentro_horario === false ? " ⚠" : ""}</span>
      : <span className="text-slate-400 text-xs">—</span> },
    { key: "acc", label: "", render: (r) => puedeEditar && (
      <Button size="sm" variant="outline" data-testid={`btn-gestion-${r.credito_id}`} onClick={(e) => { e.stopPropagation(); openAlta(r); }}>
        <Plus className="mr-1 h-3 w-3" /> Gestión
      </Button>
    )},
  ];

  const COLS_FUERA = [
    { key: "fecha", label: "Fecha", render: (r) => fdate(r.fecha) },
    { key: "hora_local_deudor", label: "Hora local", render: (r) => (
      <span className="font-mono">{r.hora_local_deudor}{" "}
        <span className="ml-1 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">FUERA</span>
      </span>
    )},
    { key: "zona_horaria_deudor", label: "Zona deudor", render: (r) => `${r.zona_horaria_deudor?.zona || "?"} (${r.zona_horaria_deudor?.tz || "?"})${r.zona_horaria_deudor?.inferido ? " · inferido" : ""}` },
    { key: "cliente", label: "Cliente" },
    { key: "numero_credito", label: "Crédito" },
    { key: "despacho_snapshot", label: "Despacho", render: (r) => r.despacho_snapshot?.razon_social || <span className="text-slate-400">Interno</span> },
    { key: "justificacion_fuera_horario", label: "Justificación", render: (r) => (
      <span className="text-xs italic text-slate-600" title={r.justificacion_fuera_horario}>
        {(r.justificacion_fuera_horario || "").slice(0, 60)}{(r.justificacion_fuera_horario || "").length > 60 ? "…" : ""}
      </span>
    )},
    { key: "capturada_por", label: "Capturada por", render: (r) => <span className="text-xs">{r.capturada_por}</span> },
  ];

  const horaCaeFuera = () => {
    if (!form?.hora_gestion) return false;
    const [h, m] = form.hora_gestion.split(":").map(Number);
    if (h < 8) return true;
    if (h > 21) return true;
    if (h === 21 && m > 0) return true;
    return false;
  };

  return (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testid="kpi-gestiones-fuera" label="Gestiones fuera de horario" value={fuera.length} icon="AlertTriangle" accent="red" />
        <StatCard testid="kpi-gestores-internos" label="Gestores internos disponibles" value={combo.internos.length} icon="Users" accent="green" />
        <StatCard testid="kpi-personas-externas" label="Personas ejecutoras externas (REDECO)" value={combo.externos.length} icon="Building2" accent="amber" />
        <StatCard testid="kpi-cartera-total-mgt" label="Créditos en cartera" value={fichas.length} icon="Wallet" accent="primary" />
      </div>

      <div>
        <h3 className="mb-2 font-heading text-sm font-semibold text-slate-700">Registrar gestión (con hora + despacho + persona)</h3>
        <DataTable testid="tabla-gestiones-cred" columns={COLS} rows={fichas.slice(0, 200)}
          emptyLabel="Sin cartera para gestionar" />
      </div>

      {fuera.length > 0 && (
        <div>
          <h3 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-red-700">
            <ShieldAlert className="h-4 w-4" /> Gestiones fuera de horario (revisión Oficial de Cumplimiento)
          </h3>
          <DataTable testid="tabla-fuera-horario" columns={COLS_FUERA} rows={fuera}
            emptyLabel="Sin gestiones fuera de horario" />
        </div>
      )}

      {/* Dialog registrar gestión */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registrar gestión de cobranza</DialogTitle>
            <DialogDescription>
              La hora debe capturarse en <b>hora local del deudor</b> (8:00–21:00). Fuera de ese rango
              se solicitará justificación escrita (no bloquea, pero deja evidencia para REDECO).
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 rounded-md bg-slate-50 p-3 text-sm">
                <div><b>{ficha?.cliente}</b> — Crédito {ficha?.numero_credito}</div>
                <div className="text-xs text-slate-500">Días de atraso: {ficha?.dias_atraso}</div>
              </div>

              <div>
                <Label>Fecha</Label>
                <Input type="date" data-testid="gest-fecha" value={form.fecha}
                  onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
              </div>
              <div>
                <Label>Hora (local deudor)</Label>
                <Input data-testid="gest-hora" type="time" value={form.hora_gestion}
                  onChange={(e) => setForm({ ...form, hora_gestion: e.target.value })}
                  className={horaCaeFuera() ? "border-red-400" : ""} />
                {horaCaeFuera() && (
                  <p data-testid="warn-fuera-horario" className="mt-1 flex items-center gap-1 text-[11px] text-red-600">
                    <AlertTriangle className="h-3 w-3" /> Fuera de 8:00–21:00 — se pedirá justificación
                  </p>
                )}
              </div>

              {municipiosSugeridos && (
                <div className="col-span-2">
                  <Label className="flex items-center gap-1">
                    Municipio del deudor
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                      {clienteInfo?.entidad_federativa} — Art. 5 con horario estacional
                    </span>
                  </Label>
                  <Select value={form.municipio || ""} onValueChange={(v) => setForm({ ...form, municipio: v })}>
                    <SelectTrigger data-testid="gest-municipio">
                      <SelectValue placeholder="Selecciona municipio (o deja vacío para revisión manual)" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="__otro__" data-testid="gest-municipio-otro">
                        Otro municipio (no fronterizo) — se usa zona estatal
                      </SelectItem>
                      {municipiosSugeridos.map((m) => (
                        <SelectItem key={m.municipio} value={m.municipio}
                          data-testid={`gest-mun-${m.municipio.replace(/\s+/g, "-")}`}>
                          {m.municipio} <span className="ml-2 text-[10px] text-slate-500">
                            (est {m.tz_estandar} · ver {m.tz_verano})
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-[11px] text-slate-500">
                    El estado del deudor tiene {municipiosSugeridos.length} municipio(s) con horario estacional (Art. 5 LHH).
                    Elige el municipio para aplicar el offset correcto.
                  </p>
                </div>
              )}

              <div className="col-span-2">
                <Label>Gestor / Despacho + persona</Label>
                <Select value={gestor} onValueChange={setGestor}>
                  <SelectTrigger data-testid="gest-gestor"><SelectValue placeholder="Selecciona quién ejecuta la gestión" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {combo.internos.length > 0 && (
                      <div className="px-2 py-1 text-[10px] font-semibold uppercase text-slate-400">Empleados internos</div>
                    )}
                    {combo.internos.map((u) => (
                      <SelectItem key={u.value} value={u.value} data-testid={`opt-int-${u.gestor_id}`}>{u.label}</SelectItem>
                    ))}
                    {combo.externos.length > 0 && (
                      <div className="border-t border-slate-100 px-2 py-1 text-[10px] font-semibold uppercase text-slate-400">Despachos externos + persona (REDECO)</div>
                    )}
                    {combo.externos.map((d) => (
                      <SelectItem key={d.value} value={d.value} data-testid={`opt-ext-${d.despacho_id}-${d.persona_ejecutora_id || "any"}`}>
                        {d.label}
                      </SelectItem>
                    ))}
                    {combo.despachos_internos.length > 0 && (
                      <div className="border-t border-slate-100 px-2 py-1 text-[10px] font-semibold uppercase text-slate-400">Despachos internos (NO REDECO)</div>
                    )}
                    {combo.despachos_internos.map((d) => (
                      <SelectItem key={d.value} value={d.value} data-testid={`opt-di-${d.despacho_id}-${d.persona_ejecutora_id || "any"}`}>
                        {d.label}
                      </SelectItem>
                    ))}
                    {combo.internos.length === 0 && combo.externos.length === 0 && combo.despachos_internos.length === 0 && (
                      <div className="px-2 py-2 text-xs text-slate-400">Da de alta un despacho en la pestaña &quot;Despachos&quot;</div>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Canal</Label>
                <Select value={form.canal} onValueChange={(v) => setForm({ ...form, canal: v })}>
                  <SelectTrigger data-testid="gest-canal"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CANALES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Resultado</Label>
                <Select value={form.resultado} onValueChange={(v) => setForm({ ...form, resultado: v })}>
                  <SelectTrigger data-testid="gest-resultado"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RESULTADOS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {form.resultado === "promesa" && (
                <>
                  <div>
                    <Label>Promesa — fecha</Label>
                    <Input type="date" data-testid="gest-prom-fecha" value={form.promesa_fecha}
                      onChange={(e) => setForm({ ...form, promesa_fecha: e.target.value })} />
                  </div>
                  <div>
                    <Label>Promesa — monto</Label>
                    <MoneyInput data-testid="gest-prom-monto" value={form.promesa_monto}
                      onChange={(v) => setForm({ ...form, promesa_monto: v })} />
                  </div>
                </>
              )}

              <div className="col-span-2">
                <Label>Notas</Label>
                <Textarea data-testid="gest-notas" rows={2} value={form.notas}
                  onChange={(e) => setForm({ ...form, notas: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Evidencia (ruta/URL)</Label>
                <Input data-testid="gest-evidencia" placeholder="Grabación, foto, correo…" value={form.evidencia}
                  onChange={(e) => setForm({ ...form, evidencia: e.target.value })} />
              </div>

              {horaCaeFuera() && (
                <div className="col-span-2 rounded-md border border-red-200 bg-red-50 p-3">
                  <Label className="text-red-900">Justificación obligatoria (fuera de horario 8:00–21:00)</Label>
                  <Textarea data-testid="gest-justificacion" rows={2} value={form.justificacion_fuera_horario}
                    onChange={(e) => setForm({ ...form, justificacion_fuera_horario: e.target.value })}
                    placeholder="Explica por qué se contactó al deudor fuera del horario permitido…" />
                </div>
              )}

              <div className="col-span-2 flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDlgOpen(false)}>Cancelar</Button>
                <Button data-testid="gest-guardar" onClick={guardar} disabled={busy}
                  className="bg-primary hover:bg-sidebar-hover">
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Registrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
